import { Bar, Doughnut } from 'react-chartjs-2';
import { useGbpSheetQuery } from '../../api/queries';
import { formatNumber } from '../../lib/format';
import { TRAFFIC_PALETTE } from '../../lib/charts';
import GbpHealthPanel from '../../components/GbpHealthPanel';
import GbpListingPanel from '../../components/GbpListingPanel';
import GbpReviewRequestsPanel from './GbpReviewRequestsPanel';
import MetricCard from '../../components/MetricCard';
import SubTabLoading from './SubTabLoading';

type Props = { af: string };

export default function GbpSubTab({ af }: Props) {
  const { data, isLoading, error } = useGbpSheetQuery(af);

  if (isLoading) {
    return (
      <SubTabLoading
        title="Loading Google Business Profile Data..."
        sub="Fetching metrics from Power My Analytics"
      />
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>GBP Data Unavailable</h3>
        <p>{(error as Error).message}</p>
      </div>
    );
  }
  if (!data?.location) {
    // No Power My Analytics data, but GBP health may still be available.
    return (
      <>
        <div className="tab-placeholder">
          <h3>No GBP usage data found</h3>
          <p>{`Views/actions metrics aren't wired up for ${af} yet — Power My Analytics not connected. The health score below scans Google Places directly and works regardless.`}</p>
        <GbpReviewRequestsPanel af={af} />
        </div>
        <GbpHealthPanel af={af} />
        <GbpListingPanel af={af} />
      </>
    );
  }

  const loc = data.location;
  const period = (
    <span style={{ fontSize: 9, fontWeight: 400, letterSpacing: '0.3px' }}> · Last 90 Days</span>
  );

  const viewsChartData = {
    labels: ['Search', 'Maps'],
    datasets: [
      {
        data: [loc.viewsSearch, loc.viewsMaps],
        backgroundColor: TRAFFIC_PALETTE.slice(0, 2),
        borderColor: 'white',
        borderWidth: 2,
      },
    ],
  };
  const actionsChartData = {
    labels: ['Website Clicks', 'Driving Directions', 'Phone Calls'],
    datasets: [
      {
        label: 'Actions',
        data: [loc.websiteClicks, loc.drivingDirections, loc.phoneCalls],
        backgroundColor: TRAFFIC_PALETTE.slice(0, 3),
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  return (
    <>
      <div className="metrics-row-5">
        <MetricCard
          label={<>Views{period}</>}
          value={formatNumber(loc.totalViews)}
          change={loc.totalViewsChange}
        />
        <MetricCard
          label={<>Views: Maps{period}</>}
          value={formatNumber(loc.viewsMaps)}
          change={loc.viewsMapsChange}
        />
        <MetricCard
          label={<>Activity{period}</>}
          value={formatNumber(loc.activity)}
          change={loc.activityChange}
        />
        <MetricCard
          label={<>Driving Directions{period}</>}
          value={formatNumber(loc.drivingDirections)}
          change={loc.drivingDirectionsChange}
        />
        <MetricCard
          label={<>Phone Calls{period}</>}
          value={formatNumber(loc.phoneCalls)}
          change={loc.phoneCallsChange}
        />
      </div>

      <GbpReviewRequestsPanel af={af} />

      <div className="charts-row">
        <div className="chart-panel">
          <h3>Where Customers View Your Business on Google (Last 90 Days)</h3>
          <div className="chart-container">
            <Doughnut
              data={viewsChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      color: '#3a4a5a',
                      font: { size: 11 },
                      padding: 12,
                      usePointStyle: true,
                      pointStyle: 'circle',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="chart-panel">
          <h3>Customer Actions (Last 90 Days)</h3>
          <div className="chart-container">
            <Bar
              data={actionsChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#8a95a5', font: { size: 12 } },
                    grid: { color: '#f0f2f5' },
                  },
                  x: {
                    ticks: { color: '#8a95a5', font: { size: 12 } },
                    grid: { display: false },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="metrics-row">
        <MetricCard
          label={<>Visit Your Website{period}</>}
          value={formatNumber(loc.websiteClicks)}
          change={loc.websiteClicksChange}
        />
        <MetricCard
          label={<>Request Directions{period}</>}
          value={formatNumber(loc.drivingDirections)}
          change={loc.drivingDirectionsChange}
        />
        <MetricCard
          label={<>Call You{period}</>}
          value={formatNumber(loc.phoneCalls)}
          change={loc.phoneCallsChange}
        />
      </div>

      <div className="metrics-footer">
        Google Business Profile — Last 90 Days • Data via Power My Analytics • {data.dateRange}
      </div>

      <GbpHealthPanel af={af} />
      <GbpListingPanel af={af} />
    </>
  );
}
